import React from "react";
import { toneColor } from "./ChartTheme";

/**
 * One card that holds several related KPI numbers.
 * items: [{ label, value, tone?, sub? }] — unchanged shape.
 * tone: default | success | warning | danger | muted
 *
 * Redesign: each metric sits in its own tinted tile on a responsive
 * grid (was flex-wrap), and a "sub" string starting with +/- renders
 * as a small trend chip instead of plain caption text.
 */
const KPIGroup = ({ title, items = [], className = "" }) => (
  <div
    className={`rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-card-dark ${className}`}
  >
    {title && (
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
        {title}
      </p>
    )}
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((item) => {
        const tone = toneColor[item.tone] || toneColor.default;
        const trendUp =
          typeof item.sub === "string" && item.sub.trim().startsWith("+");
        const trendDown =
          typeof item.sub === "string" && item.sub.trim().startsWith("-");

        return (
          <div
            key={item.label}
            className={`rounded-lg px-3 py-2.5 transition-colors ${tone.bg}`}
          >
            <p className="truncate text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
              {item.label}
            </p>
            <p
              className={`mt-0.5 text-xl font-semibold leading-tight tabular-nums ${tone.text}`}
            >
              {item.value ?? "—"}
            </p>
            {item.sub && (
              <p
                className={`mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium ${
                  trendUp
                    ? "text-emerald-600 dark:text-emerald-400"
                    : trendDown
                    ? "text-rose-600 dark:text-rose-400"
                    : "text-neutral-400 dark:text-neutral-500"
                }`}
              >
                {trendUp && "▲"}
                {trendDown && "▼"}
                {item.sub}
              </p>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export default KPIGroup;
